import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	NgZone,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';

import { environment } from '../../environments/environment';
import { AppLauncherStateService } from '../app-launcher-state.service';
import { BetaAuthService } from '../services/beta-auth.service';

const WC_WORKSPACE_KEY = 'central-wc-embed-account-resource-id';

/** Emitted by `central-web-apps-wc` when central-apps rejects the embed token. */
const WC_AUTH_REQUIRED_EVENT = 'central-apps-auth-required';

/** Emitted by `central-web-apps-wc` when the initial apps load fails. */
const WC_LOAD_ERROR_EVENT = 'central-apps-load-error';

/** Emitted by `central-web-apps-wc` when GET …/state succeeds. */
const WC_LOAD_SUCCESS_EVENT = 'central-apps-load-success';

/** How often to check whether the access token is close enough to `exp` to renew. */
const TOKEN_FRESHNESS_CHECK_MS = 60_000;

/**
 * Shortest gap between two token-rejection recoveries. Long enough that a burst of failing polls
 * cannot spin the refresh endpoint, short enough that a later, unrelated expiry still recovers.
 */
const AUTH_RECOVERY_COOLDOWN_MS = 30_000;

/**
 * Outcome of provisioning the Labs workspace the embedded surface loads from.
 *
 * `skipped` covers local builds with no central-apps base configured, where the
 * web component runs on its own seed data.
 */
type WorkspaceStatus = 'ready' | 'skipped' | 'unauthenticated' | 'unavailable';

/**
 * App Launcher working surface.
 *
 * Wraps the real, backend-wired apps management experience — the
 * `central-web-apps-wc` web component shipped from `central-ui` — in the App
 * Launcher app frame. Labs beta channel, ui-variant=labs, maxApps=1. The web
 * component renders the apps list, the empty state, and the create-app flow,
 * so the shell only provides the frame around it.
 */
@Component({
	selector: 'sd-apps-home',
	templateUrl: './apps-home.component.html',
})
export class AppsHomeComponent implements AfterViewInit, OnDestroy {
	@ViewChild('host', { static: false })
	public hostRef!: ElementRef<HTMLElement>;

	/**
	 * When true, the embedded surface could not load — hide the expanded-access
	 * upsell so it does not compete with the error card.
	 */
	public surfaceLoadFailed = false;

	private readonly staticHeadNodes: HTMLElement[] = [];
	private metaNodes: HTMLElement[] = [];
	private headResourcesInjected = false;
	private currentWcEl: (HTMLElement & Record<string, unknown>) | null = null;
	private lastAuthRecoveryAt = 0;
	private tokenSub: Subscription | null = null;
	private freshnessTimer: ReturnType<typeof setInterval> | null = null;

	constructor(
		@Inject(DOCUMENT) private readonly doc: Document,
		public readonly state: AppLauncherStateService,
		private readonly betaAuth: BetaAuthService,
		private readonly ngZone: NgZone,
		private readonly cdr: ChangeDetectorRef,
	) {}

	public async ngAfterViewInit(): Promise<void> {
		const workspaceStatus = await this.ensureBetaWorkspace();
		if (workspaceStatus === 'unauthenticated') {
			await this.state.expireSession();
			return;
		}
		if (workspaceStatus === 'unavailable') {
			this.surfaceLoadFailed = true;
		}
		this.refreshMetas();
		this.ensureHeadResources();
		this.mountWc();
		this.startTokenUpkeep();
	}

	/**
	 * Keeps the embedded surface on a live token.
	 *
	 * The web component holds whatever bearer it was given and polls with it for as long as it is
	 * mounted, so nothing else would notice the token ageing out until requests start failing. A
	 * periodic check renews ahead of `exp`, and the new value is written straight onto the element so
	 * the surface never has to be torn down and rebuilt.
	 */
	private startTokenUpkeep(): void {
		this.tokenSub = this.betaAuth.tokenChanges.subscribe((token) => {
			this.applyTokenToWc(token);
		});

		this.ngZone.runOutsideAngular(() => {
			this.freshnessTimer = setInterval(() => {
				void this.betaAuth.ensureFreshToken();
			}, TOKEN_FRESHNESS_CHECK_MS);
		});

		// A backgrounded tab throttles timers, so re-check the moment the user comes back.
		this.doc.addEventListener('visibilitychange', this.onVisibilityChange);
	}

	private readonly onVisibilityChange = (): void => {
		if (this.doc.visibilityState === 'visible') {
			void this.betaAuth.ensureFreshToken();
		}
	};

	private applyTokenToWc(token: string): void {
		if (!token) {
			return;
		}
		try {
			sessionStorage.setItem('central-wc-embed-auth-token', token);
		} catch {
			// ignore storage errors
		}
		this.setMetaContent('central-wc-auth-token', token);
		if (this.currentWcEl) {
			this.currentWcEl['authToken'] = token;
		}
	}

	private setMetaContent(name: string, content: string): void {
		for (const el of this.metaNodes) {
			if (el.getAttribute('name') === name) {
				el.setAttribute('content', content);
				return;
			}
		}
	}

	public requestAccess(): void {
		this.state.goTo('painted');
	}

	private readonly onWcAuthRequired = (): void => {
		void this.recoverFromWcAuthError();
	};

	private readonly onWcLoadError = (): void => {
		this.ngZone.run(() => {
			this.surfaceLoadFailed = true;
			this.cdr.markForCheck();
		});
	};

	private readonly onWcLoadSuccess = (): void => {
		this.ngZone.run(() => {
			this.surfaceLoadFailed = false;
			this.cdr.markForCheck();
		});
	};

	public ngOnDestroy(): void {
		this.unmountWc();
		this.tokenSub?.unsubscribe();
		this.tokenSub = null;
		if (this.freshnessTimer !== null) {
			clearInterval(this.freshnessTimer);
			this.freshnessTimer = null;
		}
		this.doc.removeEventListener('visibilitychange', this.onVisibilityChange);
		for (const el of [...this.metaNodes, ...this.staticHeadNodes]) {
			el.parentNode?.removeChild(el);
		}
		this.metaNodes.length = 0;
		this.staticHeadNodes.length = 0;
	}

	private appendToHead(el: HTMLElement, track: HTMLElement[]): void {
		el.setAttribute('data-app-launcher-wc-embed', 'true');
		const head = this.doc.head || this.doc.getElementsByTagName('head')[0];
		head.appendChild(el);
		track.push(el);
	}

	/**
	 * POST workspace/beta when no workspace is stored for this Labs session.
	 *
	 * A rejected token is retried once with a refreshed session, so an expired
	 * access token is resolved before the surface is mounted rather than after
	 * the user is looking at an error.
	 */
	private async ensureBetaWorkspace(): Promise<WorkspaceStatus> {
		const win = this.doc.defaultView;
		if (!win) {
			return 'skipped';
		}
		try {
			const existing = win.sessionStorage.getItem(WC_WORKSPACE_KEY);
			if (existing?.trim()) {
				return 'ready';
			}
		} catch {
			// continue to provision
		}

		const token = this.betaAuth.getToken();
		const apiBase = this.normalizeApiBase(environment.centralAppsApiBase || '');
		if (!token || !apiBase) {
			return 'skipped';
		}

		let res = await this.postBetaWorkspace(apiBase, token);
		if (res.status === 401) {
			const refreshed = await this.refreshBetaSession();
			if (!refreshed) {
				return 'unauthenticated';
			}
			res = await this.postBetaWorkspace(apiBase, refreshed);
			if (res.status === 401) {
				return 'unauthenticated';
			}
		}

		if (!res.workspace) {
			return 'unavailable';
		}
		try {
			win.sessionStorage.setItem(WC_WORKSPACE_KEY, res.workspace);
		} catch {
			// non-fatal: the workspace id is passed to the web component directly
		}
		return 'ready';
	}

	private async postBetaWorkspace(
		apiBase: string,
		token: string,
	): Promise<{ status: number; workspace: string }> {
		const prefix = (environment.centralAppsPathPrefix || '/v1/apps')
			.trim()
			.replace(/\/+$/, '');
		try {
			const res = await fetch(`${apiBase}${prefix}/workspace/beta`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
					'X-Central-Apps-Channel': 'beta',
				},
				body: JSON.stringify({}),
			});
			if (!res.ok) {
				console.error(
					`[app-launcher] workspace/beta failed: HTTP ${res.status}`,
				);
				return { status: res.status, workspace: '' };
			}
			const json = (await res.json()) as {
				workspace_resource_id_encoded?: unknown;
			};
			return {
				status: res.status,
				workspace:
					typeof json.workspace_resource_id_encoded === 'string'
						? json.workspace_resource_id_encoded.trim()
						: '',
			};
		} catch (e) {
			console.error('[app-launcher] workspace/beta error', e);
			return { status: 0, workspace: '' };
		}
	}

	private async refreshBetaSession(): Promise<string | null> {
		try {
			await this.betaAuth.refresh();
			return this.betaAuth.getToken();
		} catch (e) {
			console.error('[app-launcher] beta session refresh failed', e);
			return null;
		}
	}

	/**
	 * The embedded surface reported a rejected token. Refresh and hand the new one to the surface in
	 * place; if that fails, give the user a sign-in screen instead of an error card they cannot act on.
	 *
	 * Recovery is rate-limited rather than once-per-lifetime: a token that expires after a long
	 * session deserves the same second chance the first one got, but a wall of concurrent failing
	 * polls must not translate into a wall of refresh calls.
	 */
	private async recoverFromWcAuthError(): Promise<void> {
		const now = Date.now();
		if (now - this.lastAuthRecoveryAt < AUTH_RECOVERY_COOLDOWN_MS) {
			await this.state.expireSession();
			return;
		}
		this.lastAuthRecoveryAt = now;

		const token = await this.refreshBetaSession();
		if (!token) {
			await this.state.expireSession();
			return;
		}
		this.ngZone.run(() => {
			this.surfaceLoadFailed = false;
			this.cdr.markForCheck();
		});
		this.applyTokenToWc(token);
	}

	private queryParams(): URLSearchParams | null {
		const win = this.doc.defaultView;
		return win ? new URLSearchParams(win.location.search) : null;
	}

	/**
	 * Custom-domain management for the embedded web component. Production ignores the query override so
	 * the panel cannot be re-enabled — and the edge IPs re-rendered — by editing the URL.
	 */
	private resolveCustomDomainsEnabled(q: URLSearchParams | null): boolean {
		if (!environment.customDomainsEnabled) {
			return false;
		}
		const override = q?.get('customDomainsEnabled')?.trim().toLowerCase();
		if (override === 'false' || override === '0') {
			return false;
		}
		return true;
	}

	private refreshMetas(): void {
		for (const el of this.metaNodes) {
			el.parentNode?.removeChild(el);
		}
		this.metaNodes = [];

		const win = this.doc.defaultView;
		const q = win ? new URLSearchParams(win.location.search) : null;
		const session = (key: string) => {
			try {
				return win?.sessionStorage.getItem(key) ?? '';
			} catch {
				return '';
			}
		};

		const assetBase = (
			q?.get('assetBaseUrl') ||
			environment.assetBaseUrl ||
			''
		).trim();
		const apiBase = (
			q?.get('centralAppsApiBase') ||
			environment.centralAppsApiBase ||
			''
		).trim();
		const pathPrefix = (
			q?.get('centralAppsPathPrefix') ||
			environment.centralAppsPathPrefix ||
			'/v1/apps'
		).trim();
		const workspace =
			q?.get('accountResourceId') || session(WC_WORKSPACE_KEY) || '';
		const token =
			q?.get('authToken') ||
			this.betaAuth.getToken() ||
			session('central-wc-embed-auth-token') ||
			'';
		const wsUrl = (
			q?.get('centralWebsocketUrl') ||
			environment.websocketUrl ||
			''
		).trim();
		const channel = (
			q?.get('centralAppsChannel') ||
			environment.appsChannel ||
			'beta'
		).trim();
		const uiVariant = (
			q?.get('uiVariant') ||
			environment.uiVariant ||
			'labs'
		).trim();
		const maxApps = String(
			q?.get('maxApps') || environment.maxApps || 1,
		).trim();
		const customDomainsEnabled = this.resolveCustomDomainsEnabled(q);

		const metas: { name: string; content: string }[] = [
			{ name: 'central-wc-asset-base', content: assetBase },
			{ name: 'central-wc-central-apps-api-base', content: apiBase },
			{
				name: 'central-wc-central-apps-path-prefix',
				content: pathPrefix,
			},
			{ name: 'central-wc-apps-channel', content: channel },
			{ name: 'central-wc-ui-variant', content: uiVariant },
			{ name: 'central-wc-max-apps', content: maxApps },
			{
				name: 'central-wc-custom-domains-enabled',
				content: String(customDomainsEnabled),
			},
		];
		if (workspace) {
			metas.push({
				name: 'central-wc-account-resource-id',
				content: workspace,
			});
		}
		if (token) {
			metas.push({ name: 'central-wc-auth-token', content: token });
		}
		if (wsUrl) {
			metas.push({ name: 'central-wc-websocket-url', content: wsUrl });
		}
		if (environment.defaultCodebaseKind) {
			metas.push({
				name: 'central-wc-default-codebase-kind',
				content: environment.defaultCodebaseKind,
			});
		}

		for (const m of metas) {
			if (!m.content) {
				continue;
			}
			const meta = this.doc.createElement('meta');
			meta.setAttribute('name', m.name);
			meta.setAttribute('content', m.content);
			this.appendToHead(meta, this.metaNodes);
		}
	}

	private ensureHeadResources(): void {
		if (this.headResourcesInjected) {
			return;
		}

		const assetBaseUrl = (
			this.doc
				.querySelector('meta[name="central-wc-asset-base"]')
				?.getAttribute('content') ||
			environment.assetBaseUrl ||
			''
		).trim();
		if (!assetBaseUrl) {
			console.error(
				'[app-launcher] no asset base URL; not loading embed bootstrap',
			);
			return;
		}

		const link = this.doc.createElement('link');
		link.rel = 'stylesheet';
		link.href =
			'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,1,0';
		this.appendToHead(link, this.staticHeadNodes);

		const assetBase = assetBaseUrl.replace(/\/?$/, '/');
		const script = this.doc.createElement('script');
		script.src = `${assetBase}embed-wc-bootstrap.js?v=${Date.now()}`;
		this.appendToHead(script, this.staticHeadNodes);

		this.headResourcesInjected = true;
	}

	private mountWc(): void {
		const host = this.hostRef?.nativeElement;
		if (!host) {
			return;
		}
		const el = this.doc.createElement(
			'central-web-apps-wc',
		) as HTMLElement & Record<string, unknown>;
		if (environment.defaultCodebaseKind) {
			el.setAttribute(
				'default-codebase-kind',
				environment.defaultCodebaseKind,
			);
		}
		el.setAttribute('ui-variant', environment.uiVariant || 'labs');
		el.setAttribute('max-apps', String(environment.maxApps ?? 1));
		el.setAttribute(
			'custom-domains-enabled',
			String(this.resolveCustomDomainsEnabled(this.queryParams())),
		);
		el.setAttribute('empty', '');
		el.addEventListener(WC_AUTH_REQUIRED_EVENT, this.onWcAuthRequired);
		el.addEventListener(WC_LOAD_ERROR_EVENT, this.onWcLoadError);
		el.addEventListener(WC_LOAD_SUCCESS_EVENT, this.onWcLoadSuccess);
		host.appendChild(el);
		this.currentWcEl = el;

		customElements.whenDefined('central-web-apps-wc').then(() => {
			if (this.currentWcEl === el) {
				this.configureWc(el);
			}
		});
	}

	private unmountWc(): void {
		this.currentWcEl?.removeEventListener(
			WC_AUTH_REQUIRED_EVENT,
			this.onWcAuthRequired,
		);
		this.currentWcEl?.removeEventListener(
			WC_LOAD_ERROR_EVENT,
			this.onWcLoadError,
		);
		this.currentWcEl?.removeEventListener(
			WC_LOAD_SUCCESS_EVENT,
			this.onWcLoadSuccess,
		);
		this.currentWcEl?.parentNode?.removeChild(this.currentWcEl);
		this.currentWcEl = null;
	}

	private configureWc(el: HTMLElement): void {
		const win = this.doc.defaultView;
		if (!win) {
			return;
		}
		const q = new URLSearchParams(win.location.search);
		// WC host properties are set dynamically; use a loose bag for assignment.
		const wc = el as HTMLElement & {
			centralAppsApiBaseUrl?: string;
			centralAppsApiPathPrefix?: string;
			accountResourceId?: string;
			authToken?: string;
			centralWebsocketUrl?: string;
			centralAppsChannel?: string;
			uiVariant?: string;
			customDomainsEnabled?: boolean;
			maxApps?: number;
			defaultCodebaseKind?: string;
			initialApps?: unknown;
		};

		const meta = (name: string) =>
			this.doc
				.querySelector(`meta[name="${name}"]`)
				?.getAttribute('content')
				?.trim() ?? '';

		const session = (key: string) => {
			try {
				return win.sessionStorage.getItem(key) ?? '';
			} catch {
				return '';
			}
		};

		const apiBase = this.normalizeApiBase(
			meta('central-wc-central-apps-api-base') ||
				q.get('centralAppsApiBase') ||
				'',
		);
		const workspace =
			meta('central-wc-account-resource-id') ||
			q.get('accountResourceId') ||
			session(WC_WORKSPACE_KEY) ||
			'';
		const token =
			meta('central-wc-auth-token') ||
			q.get('authToken') ||
			this.betaAuth.getToken() ||
			session('central-wc-embed-auth-token') ||
			'';
		const wsUrl = this.normalizeWsUrl(
			meta('central-wc-websocket-url') ||
				q.get('centralWebsocketUrl') ||
				'',
		);
		const channelRaw = (
			meta('central-wc-apps-channel') ||
			q.get('centralAppsChannel') ||
			environment.appsChannel ||
			'beta'
		).toLowerCase();

		if (apiBase) {
			wc.centralAppsApiBaseUrl = apiBase;
		}
		const pathPrefix =
			meta('central-wc-central-apps-path-prefix') ||
			q.get('centralAppsPathPrefix') ||
			'';
		if (pathPrefix) {
			wc.centralAppsApiPathPrefix = pathPrefix.trim();
		}
		if (workspace) {
			wc.accountResourceId = workspace;
		}
		if (token) {
			wc.authToken = token;
		}
		if (wsUrl) {
			wc.centralWebsocketUrl = wsUrl;
		}
		wc.centralAppsChannel = channelRaw === 'amp' ? 'amp' : 'beta';
		wc.uiVariant =
			meta('central-wc-ui-variant') || environment.uiVariant || 'labs';
		wc.customDomainsEnabled = this.resolveCustomDomainsEnabled(q);
		const maxAppsRaw =
			meta('central-wc-max-apps') ||
			q.get('maxApps') ||
			String(environment.maxApps ?? 1);
		const maxApps = Number(maxAppsRaw);
		if (Number.isFinite(maxApps) && maxApps > 0) {
			wc.maxApps = maxApps;
		}

		const defaultCk =
			meta('central-wc-default-codebase-kind') ||
			q.get('defaultCodebaseKind') ||
			q.get('codebaseKindDefault') ||
			'';
		if (defaultCk) {
			wc.defaultCodebaseKind = defaultCk.trim();
		}

		wc.initialApps = undefined;
	}

	private normalizeApiBase(raw: string): string {
		let s = (raw || '').trim().replace(/\/+$/, '');
		if (!s) {
			return '';
		}
		if (!/^https?:\/\//i.test(s)) {
			s = `https://${s.replace(/^\/+/, '')}`;
		}
		return s.replace(/\/+$/, '');
	}

	private normalizeWsUrl(raw: string): string {
		let s = (raw || '').trim().replace(/\/+$/, '');
		if (!s) {
			return '';
		}
		if (/^wss:\/\//i.test(s)) {
			s = `https://${s.slice(6)}`;
		} else if (!/^https?:\/\//i.test(s)) {
			s = `https://${s.replace(/^\/+/, '')}`;
		}
		return s.replace(/\/+$/, '');
	}
}
